import React, { useState } from "react";
import { Sliders, Lock, ShieldCheck, FileText } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useToast } from "../../hooks/useToast";
import {
  useSettingsQuery,
  useUpdateSettingsMutation,
} from "../../api/query/useAdminQueries";
import GeneralPanel from "./settings/GeneralPanel";
import PasswordPanel from "./settings/PasswordPanel";
import PolicyPanel from "../../components/settings/PolicyPanel";

function AdminSettings() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("general");

  const { data: responseData, isLoading } = useSettingsQuery();
  const updateSettingsMutation = useUpdateSettingsMutation();

  const handlePolicySave = async (type, items) => {
    const payload =
      type === "privacy"
        ? { privacyPolicy: { items } }
        : { termsAndConditions: { items } };

    try {
      await updateSettingsMutation.mutateAsync(payload);
      toast.success(
        `${type === "privacy" ? "Privacy Policy" : "Terms & Conditions"} saved successfully.`
      );
      return true;
    } catch (error) {
      toast.error("Failed to save changes.");
      return false;
    }
  };

  const tabs = [
    { key: "general", label: "General Settings", icon: Sliders },
    { key: "password", label: "Change Password", icon: Lock },
    { key: "privacy", label: "Privacy Policy", icon: ShieldCheck },
    { key: "terms", label: "Terms & Condition", icon: FileText },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-[400px] items-center justify-center">
          <p className="text-base font-semibold text-gray-600">Loading settings...</p>
        </div>
      </AdminLayout>
    );
  }

  const settings = responseData?.data;

  return (
    <AdminLayout>
      {/* Settings Header */}
      <div className="mb-6 space-y-1">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-base text-gray-500">
          Configure system configurations and content policies
        </p>
      </div>

      {/* Tabs Container */}
      <div className="mb-6 rounded-2xl border border-[#EEF0F3] bg-white p-3 sm:p-4 shadow-sm">
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium transition ${
                  isActive
                    ? "bg-[#61013B] text-white shadow-sm"
                    : "text-[#475467] hover:bg-[#F9FAFB]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Panels */}
      {activeTab === "general" && <GeneralPanel settings={settings} />}

      {activeTab === "password" && <PasswordPanel />}

      {activeTab === "privacy" && (
        <PolicyPanel
          title="Privacy Policy"
          items={settings?.privacyPolicy?.items || []}
          lastUpdated={settings?.privacyPolicy?.lastUpdated}
          editable={true}
          onSave={(items) => handlePolicySave("privacy", items)}
        />
      )}

      {activeTab === "terms" && (
        <PolicyPanel
          title="Terms & Condition"
          items={settings?.termsAndConditions?.items || []}
          lastUpdated={settings?.termsAndConditions?.lastUpdated}
          editable={true}
          onSave={(items) => handlePolicySave("terms", items)}
        />
      )}
    </AdminLayout>
  );
}

export default AdminSettings;
